// src/components1/ThemedCalendar.js
import React, { useState, useMemo } from "react";
import "../styles/ThemedCalendar.css";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// Helper: format YYYY-MM-DD to human friendly string
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "Select Date";
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    if (isNaN(date.getTime())) return dateStr;

    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
    const monthName = date.toLocaleDateString("en-US", { month: "short" });

    if (isToday) return `Today (${dayName}, ${d} ${monthName})`;
    if (isYesterday) return `Yesterday (${dayName}, ${d} ${monthName})`;
    return `${dayName}, ${d} ${monthName} ${y}`;
  } catch {
    return dateStr;
  }
};

// Helper: get today in YYYY-MM-DD
const getTodayStr = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Helper: get yesterday in YYYY-MM-DD
const getYesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * ThemedDatePicker:
 * An interactive, mobile-optimized calendar datepicker matching the mauve/coral/eggplant theme.
 */
export const ThemedDatePicker = ({
  value,
  onChange,
  name = "date",
  label = "Date",
  required = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Parse current selected date or fallback to today
  const selectedDate = useMemo(() => {
    if (value) {
      const [y, m, d] = value.split("-").map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date();
  }, [value]);

  // Calendar view navigation (viewYear, viewMonth)
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());

  const handleOpen = () => {
    setViewYear(selectedDate.getFullYear());
    setViewMonth(selectedDate.getMonth());
    setIsOpen(true);
  };

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  // Emit change in both callback and standard event format
  const selectDate = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    const formatted = `${y}-${m}-${d}`;

    if (typeof onChange === "function") {
      onChange({
        target: { name, value: formatted },
      });
    }
    setIsOpen(false);
  };

  // Generate calendar grid days (including prev/next month buffer to fill 7 columns)
  const calendarDays = useMemo(() => {
    const firstDayIndex = new Date(viewYear, viewMonth, 1).getDay();
    const daysInCurrentMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

    const days = [];

    // Prev month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const dayNum = daysInPrevMonth - i;
      const date = new Date(viewYear, viewMonth - 1, dayNum);
      days.push({ date, isCurrentMonth: false });
    }

    // Current month days
    for (let i = 1; i <= daysInCurrentMonth; i++) {
      const date = new Date(viewYear, viewMonth, i);
      days.push({ date, isCurrentMonth: true });
    }

    // Next month padding to fill remaining 42 cells (6 rows)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(viewYear, viewMonth + 1, i);
      days.push({ date, isCurrentMonth: false });
    }

    return days;
  }, [viewYear, viewMonth]);

  const today = new Date();
  const isSelected = (d) =>
    selectedDate &&
    d.getDate() === selectedDate.getDate() &&
    d.getMonth() === selectedDate.getMonth() &&
    d.getFullYear() === selectedDate.getFullYear();

  const isToday = (d) =>
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();

  return (
    <div className="themed-datepicker-container">
      {label && <label className="mb-2 d-block" style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>{label}</label>}

      {/* Trigger Button */}
      <button
        type="button"
        className="themed-datepicker-trigger"
        onClick={handleOpen}
        aria-label="Choose date"
      >
        <div className="themed-datepicker-left">
          <span className="themed-datepicker-icon">
            <i className="fi fi-rr-calendar"></i>
          </span>
          <span className="themed-datepicker-value">{formatDisplayDate(value)}</span>
        </div>
        <i className="fi fi-rr-angle-small-down text-muted" style={{ fontSize: "14px" }}></i>
      </button>

      {/* Fallback hidden input for form validity */}
      <input type="hidden" name={name} value={value || ""} required={required} />

      {/* Calendar Bottom Sheet Modal */}
      {isOpen && (
        <div className="themed-calendar-backdrop" onClick={() => setIsOpen(false)}>
          <div
            className="themed-calendar-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="themed-calendar-drag-handle" onClick={() => setIsOpen(false)} />

            {/* Quick Presets (Today, Yesterday) */}
            <div className="themed-calendar-presets">
              <button
                type="button"
                className={`themed-calendar-preset-btn ${value === getTodayStr() ? "active" : ""}`}
                onClick={() => {
                  const now = new Date();
                  setViewYear(now.getFullYear());
                  setViewMonth(now.getMonth());
                  selectDate(now);
                }}
              >
                Today
              </button>
              <button
                type="button"
                className={`themed-calendar-preset-btn ${value === getYesterdayStr() ? "active" : ""}`}
                onClick={() => {
                  const yest = new Date();
                  yest.setDate(yest.getDate() - 1);
                  setViewYear(yest.getFullYear());
                  setViewMonth(yest.getMonth());
                  selectDate(yest);
                }}
              >
                Yesterday
              </button>
            </div>

            {/* Month / Year Navigation */}
            <div className="themed-calendar-nav">
              <button
                type="button"
                className="themed-calendar-nav-btn"
                onClick={handlePrevMonth}
                aria-label="Previous Month"
              >
                <i className="fi fi-rr-angle-left"></i>
              </button>

              <div className="themed-calendar-nav-title">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </div>

              <button
                type="button"
                className="themed-calendar-nav-btn"
                onClick={handleNextMonth}
                aria-label="Next Month"
              >
                <i className="fi fi-rr-angle-right"></i>
              </button>
            </div>

            {/* Weekdays Header */}
            <div className="themed-calendar-weekdays">
              {WEEKDAYS.map((w, idx) => (
                <div key={idx} className="themed-calendar-weekday">
                  {w}
                </div>
              ))}
            </div>

            {/* Days Grid */}
            <div className="themed-calendar-grid">
              {calendarDays.map((item, idx) => {
                const daySelected = isSelected(item.date);
                const dayIsToday = isToday(item.date);

                return (
                  <button
                    key={idx}
                    type="button"
                    className={`themed-calendar-day ${
                      !item.isCurrentMonth ? "other-month" : ""
                    } ${dayIsToday ? "today" : ""} ${daySelected ? "selected" : ""}`}
                    onClick={() => selectDate(item.date)}
                  >
                    <span>{item.date.getDate()}</span>
                    {dayIsToday && !daySelected && <div className="themed-calendar-dot" />}
                  </button>
                );
              })}
            </div>

            {/* Close / Done Button */}
            <button
              type="button"
              className="btn-coral-modern w-100"
              onClick={() => setIsOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * ThemedMonthPicker:
 * The themed pill dropdown matching the Figma design: "November 2025 v"
 */
export const ThemedMonthPicker = ({
  value,
  onChange,
  options = [],
  variant = "glass", // "glass" (for dark hero card) or "light" (for light background)
  showArrows = false, // When true, renders left and right arrow buttons to step between months
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Parse current selected year and month
  const getCurrentYM = () => {
    const today = new Date();
    if (!value || value === "current" || value === "all") {
      return { year: today.getFullYear(), month: today.getMonth() + 1 };
    }
    const parts = value.split("-").map(Number);
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { year: parts[0], month: parts[1] };
    }
    return { year: today.getFullYear(), month: today.getMonth() + 1 };
  };

  // Check if viewing current or future month (to disable next arrow)
  const today = new Date();
  const curYear = today.getFullYear();
  const curMonth = today.getMonth() + 1;
  const { year, month } = getCurrentYM();

  const isCurrentOrFuture =
    value === "current" ||
    value === "all" ||
    year > curYear ||
    (year === curYear && month >= curMonth);

  const handlePrevMonth = (e) => {
    e.stopPropagation();
    const { year: curY, month: curM } = getCurrentYM();
    const prevDate = new Date(curY, curM - 2, 1);
    const newY = prevDate.getFullYear();
    const newM = prevDate.getMonth() + 1;
    onChange(`${newY}-${newM}`);
  };

  const handleNextMonth = (e) => {
    e.stopPropagation();
    if (isCurrentOrFuture) return;
    const { year: curY, month: curM } = getCurrentYM();
    const nextDate = new Date(curY, curM, 1);
    const newY = nextDate.getFullYear();
    const newM = nextDate.getMonth() + 1;
    if (newY === curYear && newM === curMonth) {
      onChange("current");
    } else {
      onChange(`${newY}-${newM}`);
    }
  };

  // Find or format active label
  const getDisplayLabel = () => {
    if (value === "all") return "All Time";
    if (value === "current") return "Current Month";
    const found = options.find((opt) => opt.value === value);
    if (found) return found.label;
    if (value && value.includes("-")) {
      const [y, m] = value.split("-").map(Number);
      const d = new Date(y, m - 1, 1);
      return d.toLocaleString("en-US", { month: "long", year: "numeric" });
    }
    return "Select Month";
  };

  return (
    <div className="themed-month-navigator-wrap position-relative">
      {showArrows && (
        <button
          type="button"
          className={`themed-month-nav-arrow ${variant === "light" ? "light" : ""}`}
          onClick={handlePrevMonth}
          title="Previous Month"
          aria-label="Previous Month"
        >
          <i className="fi fi-rr-angle-left"></i>
        </button>
      )}

      <button
        type="button"
        className={`themed-month-pill ${variant === "light" ? "light" : ""}`}
        onClick={() => setIsOpen(true)}
      >
        <i className="fi fi-rr-calendar"></i>
        <span>{getDisplayLabel()}</span>
        <i className="fi fi-rr-angle-small-down"></i>
      </button>

      {showArrows && (
        <button
          type="button"
          className={`themed-month-nav-arrow ${variant === "light" ? "light" : ""} ${
            isCurrentOrFuture ? "disabled" : ""
          }`}
          onClick={handleNextMonth}
          disabled={isCurrentOrFuture}
          title={isCurrentOrFuture ? "Future months disabled" : "Next Month"}
          aria-label="Next Month"
        >
          <i className="fi fi-rr-angle-right"></i>
        </button>
      )}

      {isOpen && (
        <div className="themed-calendar-backdrop" onClick={() => setIsOpen(false)}>
          <div
            className="themed-calendar-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="themed-calendar-drag-handle" onClick={() => setIsOpen(false)} />

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                Select Month
              </h6>
              <button
                type="button"
                className="btn-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
                maxHeight: "320px",
                overflowY: "auto",
                marginBottom: "16px",
              }}
            >
              {options.map((opt, idx) => {
                const isActive = opt.value === value;
                return (
                  <button
                    key={idx}
                    type="button"
                    className="themed-calendar-preset-btn"
                    style={{
                      padding: "12px 10px",
                      textAlign: "center",
                      borderRadius: "14px",
                      fontWeight: isActive ? "700" : "500",
                      background: isActive
                        ? "linear-gradient(135deg, var(--accent-mauve) 0%, var(--primary-deep) 100%)"
                        : "var(--bg-color)",
                      color: isActive ? "#FFFFFF" : "var(--primary-dark)",
                      borderColor: isActive ? "transparent" : "var(--border-subtle)",
                    }}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="btn-coral-modern w-100"
              onClick={() => setIsOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemedDatePicker;
